// src/components/rewards/ChestOpener.js
//
// Opening a chest: it bounces ("Tap to open!"), shakes while the server
// rolls what's inside (functions/chests.js), bursts open with light rays,
// then the prizes pop out one by one — coins (which fly into the coin
// counter), a power-up, stickers (NEW! or "+2 coins" for one you already
// have), and sometimes a free avatar item.
//
// Props: chest { id, tier, label }, onClose(), onDone(contents)

import ModalPortal from "../common/ModalPortal";
import React, { useEffect, useRef, useState } from "react";
import ChestArt from "./ChestArt";
import { Coin } from "../coins/CoinCounter";
import { ItemArt, STARTER_ITEMS } from "../avatar/wearableArt";
import { AvatarFace, DEFAULT_LOOK } from "../avatar/avatarParts";
import { CATALOG_BY_ID } from "../avatar/avatarCatalog";
import { openChest, REWARDS } from "../../services/rewardsService";
import "./rewards.css";

const POWERUPS = Object.fromEntries(STARTER_ITEMS.filter((i) => i.kind === "powerup").map((i) => [i.id, i]));

const itemPreview = (id) => {
  const it = CATALOG_BY_ID[id];
  if (!it) return { ...DEFAULT_LOOK };
  const key = { glasses: "glasses", sunglasses: "glasses", pin: "pin", top: "top", color: "topColor", haircolor: "hairColor", eyes: "eyesItem", mouth: "mouthItem", mustache: "mustache", pants: "pants", shoes: "shoes" }[it.kind];
  return { ...DEFAULT_LOOK, [key]: id };
};

const ChestOpener = ({ chest, onClose, onDone }) => {
  const [phase, setPhase] = useState("closed"); // closed | opening | open | error
  const [contents, setContents] = useState(null);
  const [shown, setShown] = useState(0);
  const [err, setErr] = useState("");
  const chestRef = useRef(null);
  const tier = chest?.tier || "wood";

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && phase !== "opening" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, phase]);

  const prizes = [];
  if (contents) {
    prizes.push({ key: "coins", kind: "coins" });
    if (contents.powerup) prizes.push({ key: "pu", kind: "powerup" });
    (contents.stickers || []).forEach((s, i) => prizes.push({ key: `s${i}`, kind: "sticker", s }));
    if (contents.item) prizes.push({ key: "item", kind: "item" });
  }

  // reveal prizes one at a time
  useEffect(() => {
    if (phase !== "open" || shown >= prizes.length) return undefined;
    const t = setTimeout(() => setShown((n) => n + 1), shown === 0 ? 450 : 380);
    return () => clearTimeout(t);
  }, [phase, shown, prizes.length]);

  const open = async () => {
    if (phase !== "closed") return;
    setPhase("opening");
    const started = Date.now();
    try {
      const out = await openChest(chest.id);
      const wait = Math.max(0, 1100 - (Date.now() - started)); // let the shake play
      setTimeout(() => {
        setContents(out);
        setPhase("open");
        const r = chestRef.current?.getBoundingClientRect();
        if (out?.coins > 0) {
          try {
            window.dispatchEvent(
              new CustomEvent("sbl:coins-earned", {
                detail: { amount: out.coins, coins: out.balance, reason: "chest", from: r ? { x: r.left + r.width / 2, y: r.top + r.height / 2 } : undefined },
              })
            );
          } catch {
            /* no window */
          }
        }
        // everything on the page (coin counters, sticker albums, the gift
        // list, the avatar closet) updates now — no refresh needed
        try {
          window.dispatchEvent(new CustomEvent("sbl:rewards-changed", { detail: { chestId: chest.id, ...out } }));
        } catch {
          /* no window */
        }
        onDone?.(out);
      }, wait);
    } catch (e) {
      setErr(e?.message || "The chest is stuck! Try again in a moment.");
      setPhase("error");
    }
  };

  const name = REWARDS.chests[tier]?.name || "Chest";

  return (
    <ModalPortal>
      <div className="sbl-rw-pop" role="dialog" aria-modal="true" aria-label={name} onMouseDown={(e) => e.target === e.currentTarget && phase !== "opening" && onClose?.()}>
        <div className={`sbl-chest-stage is-${tier}`}>
          <button type="button" className="sbl-rw-x" onClick={onClose} aria-label="Close" disabled={phase === "opening"}>
            ✕
          </button>
          <h2>{name}</h2>
          {chest?.label && <p className="sbl-chest-from">{chest.label}</p>}

          <button
            type="button"
            ref={chestRef}
            className={`sbl-chest-btn is-${phase}`}
            onClick={open}
            disabled={phase !== "closed"}
            aria-label={phase === "closed" ? "Open the chest" : name}
          >
            <ChestArt tier={tier} open={phase === "open"} />
          </button>

          {phase === "closed" && <p className="sbl-chest-hint">Tap the chest to open it!</p>}
          {phase === "opening" && <p className="sbl-chest-hint">Opening…</p>}
          {phase === "error" && <p className="sbl-chest-hint is-bad">{err}</p>}

          {phase === "open" && (
            <>
              <div className="sbl-prizes">
                {prizes.slice(0, shown).map((p) => {
                  if (p.kind === "coins")
                    return (
                      <div key={p.key} className="sbl-prize is-coins">
                        <Coin size={44} />
                        <b>+{contents.coins}</b>
                        <span>coins</span>
                      </div>
                    );
                  if (p.kind === "powerup") {
                    const it = POWERUPS[contents.powerup];
                    return (
                      <div key={p.key} className="sbl-prize is-powerup">
                        <span className="sbl-prize-art">{it ? <ItemArt item={it} /> : "⚡"}</span>
                        <b>{it?.name || "Power-up"}</b>
                        <span>power-up</span>
                      </div>
                    );
                  }
                  if (p.kind === "sticker")
                    return (
                      <div key={p.key} className={`sbl-prize is-sticker ${p.s.rare ? "is-rare" : ""}`}>
                        <span className="sbl-sticker" aria-hidden="true">
                          {p.s.emoji}
                        </span>
                        <b>{p.s.name}</b>
                        <span>{p.s.isNew ? (p.s.rare ? "RARE · NEW!" : "NEW sticker!") : `+${contents.dupCoins && contents.stickers ? Math.round(contents.dupCoins / Math.max(1, contents.stickers.filter((x) => !x.isNew).length)) : REWARDS.duplicateCoins} coins (have it)`}</span>
                      </div>
                    );
                  const it = CATALOG_BY_ID[contents.item];
                  return (
                    <div key={p.key} className="sbl-prize is-item">
                      <span className="sbl-prize-face">
                        <AvatarFace look={itemPreview(contents.item)} />
                      </span>
                      <b>{it?.name || "Avatar item"}</b>
                      <span>free for your avatar!</span>
                    </div>
                  );
                })}
              </div>
              {shown >= prizes.length && (
                <button type="button" className="sbl-rw-btn" onClick={onClose}>
                  Collect ✓
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </ModalPortal>
  );
};

export default ChestOpener;
